import React, { useEffect, useState } from 'react';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import topUpQrImage from '../../assets/ai-credit-topup-qr.png';
import './agent.css';
import {
  GET_AI_AGENTS,
  GET_AI_CREDIT_USAGE,
  GET_POLL_STATUS,
  STOP_POLL_JOB,
} from '../../api/aiKnowledge.service';
import TableActionsContainer from '../../components/CustomTable/TableActionsContainer';
import { CustomButton } from '../../components/CustomButton';
import SklbtConfirmation from '../../components/common/SklbtConfirmation';
import { useLayout } from '../../context/LayoutContext';
import { deleteKnowledge } from '../../reducers/aiKnowledgeSlice';

const Agent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { toastMessage } = useLayout();
  const user = useSelector((state) => state.auth?.user);
  const { deleteLoading, deletingId, deleteError } = useSelector(
    (state) => state.aiKnowledge,
  );
  const [loading, setLoading] = useState(false);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState('');
  const [usageSummary, setUsageSummary] = useState({
    companyId: null,
    credit: null,
    usedCredit: null,
    remainingCredit: null,
    inboundCount: null,
    outboundCount: null,
    createdAt: null,
    updatedAt: null,
  });
  const [knowledgeRows, setKnowledgeRows] = useState([]);
  const [knowledgeListError, setKnowledgeListError] = useState('');
  const [pollLoadingId, setPollLoadingId] = useState('');
  const [stopLoadingId, setStopLoadingId] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const [showTopUpModal, setShowTopUpModal] = useState(false);

  const getActionMessage = (payload, fallback) => {
    const normalizeMessage = (value) =>
      Array.isArray(value) ? value.filter(Boolean).join(', ') : value;

    if (typeof payload === 'string') return payload;
    return (
      normalizeMessage(payload?.response?.data?.message) ||
      normalizeMessage(payload?.data?.message) ||
      normalizeMessage(payload?.message) ||
      normalizeMessage(payload?.error) ||
      fallback
    );
  };

  const showActionToast = (severity, payload, fallback) => {
    toastMessage({
      severity,
      detail: getActionMessage(payload, fallback),
    });
  };

  const normalizeMetric = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const normalizeUsageSummary = (payload) => {
    const row = Array.isArray(payload)
      ? payload[0]
      : Array.isArray(payload?.data)
        ? payload.data[0]
        : Array.isArray(payload?.rows)
          ? payload.rows[0]
          : Array.isArray(payload?.data?.rows)
            ? payload.data.rows[0]
            : payload?.data || payload;

    const credit = normalizeMetric(row?.credit);
    const usedCredit = normalizeMetric(row?.usedCredit);
    const remainingCredit =
      normalizeMetric(row?.remainingCredit) ??
      (credit !== null && usedCredit !== null ? credit - usedCredit : null);

    return {
      companyId: row?.companyId || user?.companyId || null,
      credit,
      usedCredit,
      remainingCredit,
      inboundCount: normalizeMetric(row?.inboundCount),
      outboundCount: normalizeMetric(row?.outboundCount),
      createdAt: row?.createdAt || null,
      updatedAt: row?.updatedAt || null,
    };
  };

  const formatUsageMetric = (value) => {
    if (value === null || value === undefined) return '--';
    return Number(value).toLocaleString();
  };

  const normalizeDirectoryRows = (rows) => {
    const grouped = new Map();
    const normalized = [];

    const pushGroup = (agentData, knowledgeList = []) => {
      const key = String(agentData?.id || agentData?.agentId || '');
      if (!key) return;
      if (!grouped.has(key)) {
        grouped.set(key, {
          agent: {
            id: agentData?.id || agentData?.agentId || '',
            agentName: agentData?.agentName || '',
            providerName: agentData?.providerName || '',
            llmModel: agentData?.llmModel || '',
            isActive: Number(agentData?.isActive) === 1,
            companyId: agentData?.companyId || '',
            createdAt: agentData?.createdAt || null,
            updatedAt: agentData?.updatedAt || null,
          },
          knowledge: [],
        });
      }
      const entry = grouped.get(key);
      knowledgeList.forEach((knowledge) => {
        entry.knowledge.push({
          id: knowledge?.id || '',
          companyId: knowledge?.companyId || '',
          agentId: knowledge?.agentId || entry.agent.id,
          sourceName: knowledge?.sourceName || '',
          knowledgeType: knowledge?.knowldgeType || knowledge?.knowledgeType || '',
          url: knowledge?.KnowledgeUrl || '',
          jobId: knowledge?.jobId || '',
          jobStatus: knowledge?.jobStatus || '',
          createdAt: knowledge?.createdAt || null,
          updatedAt: knowledge?.updatedAt || null,
        });
      });
    };

    rows.forEach((item) => {
      if (item?.agent || item?.knowledge) {
        const agentData = item?.agent || {};
        const knowledgeList = Array.isArray(item?.knowledge) ? item.knowledge : [];
        pushGroup(agentData, knowledgeList);
        return;
      }

      const agentData = {
        id: item?.agentId || item?.id || '',
        agentName: item?.agentName || '',
        providerName: item?.providerName || '',
        llmModel: item?.llmModel || '',
        isActive: Number(item?.isActive) === 1,
        companyId: item?.companyId || '',
        createdAt: item?.createdAt || null,
        updatedAt: item?.updatedAt || null,
      };
      const knowledgeList = item?.knowledgeId
        ? [
            {
              id: item?.knowledgeId || item?.id || '',
              companyId: item?.companyId || '',
              agentId: item?.agentId || item?.id || '',
              sourceName: item?.sourceName || '',
              knowldgeType: item?.knowldgeType || item?.knowledgeType || '',
              KnowledgeUrl: item?.KnowledgeUrl || '',
              jobId: item?.jobId || '',
              jobStatus: item?.jobStatus || '',
              createdAt: item?.createdAt || null,
              updatedAt: item?.updatedAt || null,
            },
          ]
        : [];
      pushGroup(agentData, knowledgeList);
    });

    grouped.forEach((value) => {
      normalized.push(value);
    });

    return normalized;
  };

  const getKnowledgeAvailability = (knowledgeList = []) => {
    const knowledgeTypes = knowledgeList
      .map((knowledge) => String(knowledge?.knowledgeType || '').toLowerCase().trim())
      .filter(Boolean);

    return {
      hasWebsite: knowledgeTypes.some((type) => type.includes('website')),
      hasDocument: knowledgeTypes.some((type) => type.includes('document')),
    };
  };

  const fetchAgents = async () => {
    setLoading(true);
    setKnowledgeListError('');
    try {
      const resp = await GET_AI_AGENTS();
      const rows = Array.isArray(resp?.data)
        ? resp.data
        : Array.isArray(resp?.data?.data)
          ? resp.data.data
          : resp?.data?.rows || [];
      const normalized = normalizeDirectoryRows(rows);
      setKnowledgeRows(normalized);
      return normalized;
    } catch (err) {
      setKnowledgeRows([]);
      setKnowledgeListError(err?.response?.data?.message || 'Failed to load agents.');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageSummary = async () => {
    setUsageLoading(true);
    setUsageError('');
    try {
      const resp = await GET_AI_CREDIT_USAGE();
      setUsageSummary(normalizeUsageSummary(resp));
    } catch (err) {
      setUsageError(err?.response?.data?.message || '');
    } finally {
      setUsageLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
    fetchUsageSummary();
  }, []);

  useEffect(() => {
    if (user) {
      fetchUsageSummary();
    }
  }, [user?.companyId]);

  useEffect(() => {
    if (!deleteError) return;
    const message =
      deleteError?.message || deleteError?.error || deleteError || 'Failed to delete knowledge.';
    setKnowledgeListError(message);
  }, [deleteError]);

  const handlePollJob = async (rowData) => {
    if (!rowData?.jobId) return;
    setPollLoadingId(String(rowData.jobId));
    setKnowledgeListError('');
    try {
      await GET_POLL_STATUS(rowData.jobId);
      await fetchAgents();
    } catch (err) {
      setKnowledgeListError(err?.response?.data?.message || 'Failed to poll job.');
    } finally {
      setPollLoadingId('');
    }
  };

  const handleStopJob = async (rowData) => {
    if (!rowData?.jobId) return;
    setStopLoadingId(String(rowData.jobId));
    setKnowledgeListError('');
    try {
      const resp = await STOP_POLL_JOB(rowData.jobId);
      await fetchAgents();
      showActionToast('success', resp, 'Job stopped successfully.');
    } catch (err) {
      const message = getActionMessage(err, 'Failed to stop job.');
      setKnowledgeListError(message);
      showActionToast('error', err, message);
    } finally {
      setStopLoadingId('');
    }
  };

  const handleDeleteKnowledge = async (rowData) => {
    if (!rowData?.id) return;
    await dispatch(deleteKnowledge({ id: rowData.id }))
      .unwrap()
      .then(async (resp) => {
        await fetchAgents();
        showActionToast('success', resp?.data, 'Knowledge deleted successfully.');
      })
      .catch((err) => {
        const message = getActionMessage(err, 'Failed to delete knowledge.');
        setKnowledgeListError(message);
        showActionToast('error', err, message);
      });
  };

  const openConfirm = (type, rowData) => {
    setConfirmAction({ type, rowData });
  };

  const handleConfirm = async () => {
    if (!confirmAction) return;
    const { type, rowData } = confirmAction;
    setConfirmAction(null);

    if (type === 'stop') {
      await handleStopJob(rowData);
      return;
    }

    if (type === 'delete') {
      await handleDeleteKnowledge(rowData);
    }
  };

  const handleReject = () => {
    setConfirmAction(null);
  };

  const handleAddKnowledgeFromRow = (rowData) => {
    if (!rowData?.agent?.id) return;
    const params = new URLSearchParams();
    params.set('step', '2');
    params.set('agentId', String(rowData.agent.id));
    if (rowData.agent.agentName) {
      params.set('agentName', rowData.agent.agentName);
    }
    navigate(`/ai-agent/new?${params.toString()}`);
  };

  const handleCreateAgent = () => {
    navigate('/ai-agent/new');
  };

  const openTopUpModal = () => {
    setShowTopUpModal(true);
  };

  const closeTopUpModal = () => {
    setShowTopUpModal(false);
  };

  const knowledgeAgentTemplate = (agent) => (
    <div>
      <div className="agent-ui__agent-name">{agent?.agentName || '-'}</div>
      {agent?.id ? (
        <div className="agent-ui__agent-id">#{String(agent.id).padStart(3, '0')}</div>
      ) : null}
    </div>
  );

  const knowledgeUrlTemplate = (rowData) => {
    if (!rowData?.url) return <span>-</span>;

    return (
      <a
        href={rowData.url}
        target="_blank"
        rel="noopener noreferrer"
        className="agent-ui__source-link"
      >
        Knowledge Document
      </a>
    );
  };

  const formatDateTime = (value) => {
    if (!value) return '-';
    const text = new Date(value).toLocaleString();
    const parts = String(text).split(',');
    if (parts.length < 2) return text;
    return (
      <div className="agent-ui__date">
        <div>{parts[0]}</div>
        <div>{parts.slice(1).join(',').trim()}</div>
      </div>
    );
  };

  const knowledgeStatusTemplate = (rowData) => (
    <span
      className={`agent-ui__status ${
        rowData.isActive ? 'agent-ui__status--active' : 'agent-ui__status--paused'
      }`}
    >
      {rowData.isActive ? 'Active' : 'Inactive'}
    </span>
  );

  const getJobStatusVariant = (status) => {
    const normalizedStatus = String(status || '').toLowerCase().trim();

    if (!normalizedStatus) return 'default';
    if (normalizedStatus.includes('completed')) return 'success';
    if (
      normalizedStatus.includes('cancelled') ||
      normalizedStatus.includes('canceled') ||
      normalizedStatus.includes('canceld') ||
      normalizedStatus.includes('cancel') ||
      normalizedStatus.includes('cancle')
    ) {
      return 'warning';
    }
    if (normalizedStatus.includes('failed') || normalizedStatus.includes('error')) {
      return 'danger';
    }

    return 'info';
  };

  const jobStatusTemplate = (rowData) => (
    <span className={`agent-ui__pill agent-ui__pill--${getJobStatusVariant(rowData?.jobStatus)}`}>
      {rowData?.jobStatus || '-'}
    </span>
  );

  const showCreateAgentButton = knowledgeRows.length < 2;

  const knowledgeCountTemplate = (rowData) => {
    const knowledgeList = Array.isArray(rowData?.knowledge) ? rowData.knowledge : [];
    const { hasWebsite, hasDocument } = getKnowledgeAvailability(knowledgeList);
    const availability = [];

    if (hasWebsite) availability.push('Website');
    if (hasDocument) availability.push('Document');

    return (
      <div className="agent-ui__metric">
        <div className="agent-ui__metric-value">{knowledgeList.length}</div>
        <div className="agent-ui__metric-hint">
          {availability.length ? availability.join(' / ') : 'No sources'}
        </div>
      </div>
    );
  };

  const agentActionTemplate = (rowData) => {
    const knowledgeList = Array.isArray(rowData?.knowledge) ? rowData.knowledge : [];
    const { hasWebsite, hasDocument } = getKnowledgeAvailability(knowledgeList);
    const canAddKnowledge = !hasWebsite || !hasDocument;

    if (!canAddKnowledge) {
      return null;
    }

    return (
      <CustomButton
        varient="outline"
        label="Add Knowledge"
        icon="pi pi-plus"
        onClick={() => handleAddKnowledgeFromRow(rowData)}
        disabled={!rowData?.agent?.id}
      />
    );
  };

  const knowledgeActionTemplate = (knowledge) => {
    const jobStatusValue = String(knowledge?.jobStatus || '')
      .toLowerCase()
      .trim();
    const jobIdValue = knowledge?.jobId ? String(knowledge.jobId) : '';
    const isPollingRow = !!jobIdValue && pollLoadingId === jobIdValue;
    const isStoppingRow = !!jobIdValue && stopLoadingId === jobIdValue;
    const isDeletingRow =
      !!knowledge?.id && deleteLoading && String(deletingId) === String(knowledge.id);
    const isCancelled =
      jobStatusValue.includes('cancelled') ||
      jobStatusValue.includes('canceled') ||
      jobStatusValue.includes('canceld') ||
      jobStatusValue.includes('cancel') ||
      jobStatusValue.includes('cancle');
    const isCompleted = jobStatusValue.includes('completed');
    const showRefresh = !!jobIdValue && !isCancelled && !isCompleted;
    const showStop = !!jobIdValue && !isCancelled && !isCompleted;

    return (
      <div className="agent-ui__knowledge-actions">
        {showRefresh ? (
          <CustomButton
            varient="icon-button"
            icon={isPollingRow ? 'pi pi-spin pi-refresh' : 'pi pi-refresh'}
            onClick={() => handlePollJob(knowledge)}
            disabled={!jobIdValue || isPollingRow}
            tooltip={isPollingRow ? 'Refreshing...' : 'Refresh'}
            style={{ padding: '0px', width: '2rem', height: '2rem', justifyContent: 'center' }}
          />
        ) : null}
        {showStop ? (
          <CustomButton
            varient="icon-button"
            icon={isStoppingRow ? 'pi pi-spin pi-spinner' : 'pi pi-stop-circle'}
            onClick={() => openConfirm('stop', knowledge)}
            disabled={!jobIdValue || isStoppingRow}
            tooltip={isStoppingRow ? 'Stopping...' : 'Stop Job'}
            style={{ padding: '0px', width: '2rem', height: '2rem', justifyContent: 'center' }}
          />
        ) : null}
        <CustomButton
          varient="icon-button"
          icon={isDeletingRow ? 'pi pi-spin pi-spinner' : 'pi pi-trash'}
          onClick={() => openConfirm('delete', knowledge)}
          disabled={!knowledge?.id || isDeletingRow}
          tooltip={isDeletingRow ? 'Deleting...' : 'Delete Knowledge'}
          style={{ padding: '0px', width: '2rem', height: '2rem', justifyContent: 'center' }}
        />
      </div>
    );
  };

  const knowledgeExpansionTemplate = (rowData) => {
    const agent = rowData?.agent || {};
    const knowledgeList = Array.isArray(rowData?.knowledge) ? rowData.knowledge : [];

    return (
      <div className="agent-ui__expansion">
        <div className="agent-ui__expansion-head">
          <div>
            <div className="agent-ui__expansion-title">Knowledge Information</div>
            <div className="agent-ui__expansion-subtitle">
              Sources and ingestion jobs for {agent?.agentName || 'this agent'}.
            </div>
          </div>
        </div>

        <DataTable
          value={knowledgeList}
          className="skalebot-table agent-ui__knowledge-datatable"
          emptyMessage="No knowledge found."
          tableStyle={{ width: '100%' }}
        >
          <Column
            field="sourceName"
            header="Source Name"
            body={(knowledge) => knowledge?.sourceName || '-'}
          />
          <Column
            field="knowledgeType"
            header="Type"
            body={(knowledge) => knowledge?.knowledgeType || '-'}
          />
          <Column header="Knowledge" body={knowledgeUrlTemplate} />
          <Column header="Job Status" body={jobStatusTemplate} />
          <Column header="Created" body={(knowledge) => formatDateTime(knowledge?.createdAt)} />
          <Column header="Updated" body={(knowledge) => formatDateTime(knowledge?.updatedAt)} />
          <Column
            header="Actions"
            body={knowledgeActionTemplate}
            bodyStyle={{ minWidth: '8rem' }}
          />
        </DataTable>
      </div>
    );
  };

  const usageCards = [
    {
      label: 'AI Credit',
      value: formatUsageMetric(usageSummary.credit),
      hint: 'Total allocated credits',
    },
    {
      label: 'Used Credit',
      value: formatUsageMetric(usageSummary.usedCredit),
      hint: 'Credits already consumed',
    },
    {
      label: 'Remaining Credit',
      value: formatUsageMetric(usageSummary.remainingCredit),
      hint: 'Credits currently available',
    },
    // {
    //   label: 'Inbound Count',
    //   value: formatUsageMetric(usageSummary.inboundCount),
    //   hint: 'Inbound requests tracked',
    // },
    // {
    //   label: 'Outbound Count',
    //   value: formatUsageMetric(usageSummary.outboundCount),
    //   hint: 'Outbound requests tracked',
    // },
  ];

  const topUpSteps = [
    {
      title: 'Open your UPI app',
      description: 'Use BHIM, Google Pay, PhonePe, Paytm, or any bank UPI app.',
    },
    {
      title: 'Scan the QR code',
      description: 'Scan the Deskala QR below to start the AI credit payment.',
    },
    {
      title: 'Enter your amount',
      description: 'Type the amount you want to pay for AI credit top up.',
    },
    {
      title: 'Complete the payment',
      description: 'Confirm the payment and keep the transaction reference for safety.',
    },
  ];

  const topUpNotes = [
    'Please verify that the merchant name shows as Deskala Research & Design before making payment.',
    'If credit update takes time, keep your payment reference handy for verification.',
  ];

  const topUpPlans = [
    {
      amount: 'INR 500',
      credits: '2,000 credits',
      rate: '4 credits / INR',
      isBestValue: false,
    },
    {
      amount: 'INR 1000',
      credits: '5,000 credits',
      rate: '5 credits / INR',
      isBestValue: false,
    },
    {
      amount: 'INR 5000',
      credits: '30,000 credits',
      rate: '6 credits / INR',
      isBestValue: true,
    },
  ];

  const topUpModalFooter = (
    <div className="agent-ui__topup-footer">
      <CustomButton varient="outline" label="Close" onClick={closeTopUpModal} />
    </div>
  );

  return (
    <div className="agent-ui__shell">
      <div className="agent-ui__topbar">
        <div className="agent-ui__topbar-copy">
          <div className="agent-ui__title">AI Agents</div>
          <div className="agent-ui__subtitle">
            Manage agent configurations, knowledge sources, and ingestion jobs.
          </div>
        </div>
      </div>

      <SklbtConfirmation
        visible={Boolean(confirmAction)}
        header={confirmAction?.type === 'delete' ? 'Delete Knowledge' : 'Stop Job'}
        message={
          confirmAction?.type === 'delete'
            ? 'Are you sure you want to delete this knowledge source?'
            : 'Are you sure you want to stop this job?'
        }
        handleConfirm={handleConfirm}
        handleReject={handleReject}
      />

      <Dialog
        visible={showTopUpModal}
        onHide={closeTopUpModal}
        modal
        header=""
        closable={false}
        draggable={false}
        resizable={false}
        footer={topUpModalFooter}
        className="agent-ui__topup-dialog"
        style={{ width: 'min(94vw, 980px)' }}
      >
        <div className="agent-ui__topup-modal agent-ui__topup-modal--split">
          <div className="agent-ui__topup-side agent-ui__topup-side--plans">
            <div className="agent-ui__topup-copy agent-ui__topup-copy--left">
              <div className="agent-ui__topup-kicker">AI Credit Plans</div>
              <div className="agent-ui__topup-title">Choose a top up amount</div>
              <div className="agent-ui__topup-text">
                Pick the plan that fits your AI usage. Higher plans give you better credit value.
              </div>
            </div>

            <div className="agent-ui__topup-plan-grid">
              {topUpPlans.map((plan) => (
                <div
                  key={plan.amount}
                  className={`agent-ui__topup-plan-card ${
                    plan.isBestValue ? 'agent-ui__topup-plan-card--featured' : ''
                  }`}
                >
                  <div className="agent-ui__topup-plan-head">
                    <div className="agent-ui__topup-plan-amount">{plan.amount}</div>
                    {plan.isBestValue ? (
                      <div className="agent-ui__topup-plan-badge">
                        <i className="pi pi-star-fill" />
                        <span>Best value</span>
                      </div>
                    ) : null}
                  </div>
                  <div className="agent-ui__topup-plan-credits">{plan.credits}</div>
                  <div className="agent-ui__topup-plan-rate">{plan.rate}</div>
                </div>
              ))}
            </div>

            <div className="agent-ui__topup-note-box">
              {topUpNotes.map((note) => (
                <div key={note} className="agent-ui__topup-note">
                  <i className="pi pi-info-circle" />
                  <span>{note}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="agent-ui__topup-side agent-ui__topup-side--payment">
            <div className="agent-ui__topup-copy">
              <div className="agent-ui__topup-kicker">UPI Payment Accepted Here</div>
              <div className="agent-ui__topup-title">Pay for AI Credit Top Up</div>
              <div className="agent-ui__topup-text">
                Scan the QR code below to make a quick UPI payment for your AI credit recharge.
              </div>
            </div>

            <div className="agent-ui__topup-qr-shell">
              <img
                src={topUpQrImage}
                alt="AI credit top up QR code"
                className="agent-ui__topup-qr-image"
              />
            </div>

            <div className="agent-ui__topup-merchant">
              <span>Merchant Name :</span>
              <strong>Deskala</strong>
            </div>

            <div className="agent-ui__topup-steps">
              {topUpSteps.map((step, index) => (
                <div key={step.title} className="agent-ui__topup-step">
                  <div className="agent-ui__topup-step-index">{index + 1}</div>
                  <div className="agent-ui__topup-step-copy">
                    <div className="agent-ui__topup-step-title">{step.title}</div>
                    <div className="agent-ui__topup-step-text">{step.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Dialog>

      <div className="agent-ui__list">
        <div className="agent-ui__usage-panel">
          <div className="agent-ui__usage-layout">
            <div className="agent-ui__usage-strip">
              <div className="agent-ui__usage-grid">
                {usageCards.map((card) => (
                  <div key={card.label} className="agent-ui__usage-card">
                    <div className="agent-ui__usage-label">{card.label}</div>
                    <div className="agent-ui__usage-value">{usageLoading ? ' ' : card.value}</div>
                    <div className="agent-ui__usage-hint">{card.hint}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="agent-ui__usage-actions">
              <CustomButton
                varient="filled"
                label="AI Credit Top Up"
                icon="pi pi-qrcode"
                onClick={openTopUpModal}
              />
            </div>
          </div>

          {usageError ? <div className="agent-ui__usage-error">{usageError}</div> : null}
        </div>

        <div className="agent-ui__directory">
          <TableActionsContainer>
            <div className="agent-ui__directory-header">
              <div>
                <div className="agent-ui__directory-title">Agent Directory</div>
                <div className="agent-ui__directory-subtitle">
                  Each agent is shown separately with its linked knowledge records.
                </div>
              </div>
              <div className="agent-ui__directory-actions">
                {showCreateAgentButton ? (
                  <CustomButton
                    varient="filled"
                    label="Add AI Agent"
                    icon="pi pi-plus"
                    onClick={handleCreateAgent}
                  />
                ) : null}
              </div>
            </div>
          </TableActionsContainer>

          <div className="agent-ui__directory-body">
            {knowledgeListError && knowledgeRows.length ? (
              <div className="agent-ui__directory-error">{knowledgeListError}</div>
            ) : null}

            {loading ? (
              <div className="agent-ui__empty">Loading agents...</div>
            ) : knowledgeRows.length === 0 ? (
              <div className="agent-ui__empty">
                {knowledgeListError ? knowledgeListError : 'No agents found.'}
              </div>
            ) : (
              <div className="agent-ui__cardlist">
                {knowledgeRows.map((row) => {
                  const agent = row?.agent || {};
                  const knowledgeList = Array.isArray(row?.knowledge) ? row.knowledge : [];

                  return (
                    <div key={agent?.id || agent?.agentName} className="agent-ui__info-card">
                      <div className="agent-ui__info-header">
                        <div className="agent-ui__info-title">{knowledgeAgentTemplate(agent)}</div>
                        <div className="agent-ui__info-header-actions">{agentActionTemplate(row)}</div>
                      </div>

                      <div className="agent-ui__info-grid">
                        <div className="agent-ui__info-item">
                          <div className="agent-ui__info-label">Provider</div>
                          <div className="agent-ui__info-value">{agent?.providerName || '-'}</div>
                        </div>
                        <div className="agent-ui__info-item">
                          <div className="agent-ui__info-label">Model</div>
                          <div className="agent-ui__info-value">{agent?.llmModel || '-'}</div>
                        </div>
                        <div className="agent-ui__info-item">
                          <div className="agent-ui__info-label">Status</div>
                          <div className="agent-ui__info-value">{knowledgeStatusTemplate(agent)}</div>
                        </div>
                        <div className="agent-ui__info-item">
                          <div className="agent-ui__info-label">Knowledge</div>
                          <div className="agent-ui__info-value">{knowledgeCountTemplate(row)}</div>
                        </div>
                      </div>

                      {knowledgeList.length ? (
                        knowledgeExpansionTemplate(row)
                      ) : (
                        <div className="agent-ui__expansion">
                          <div className="agent-ui__empty agent-ui__empty--compact">
                            No knowledge found for this agent.
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Agent;
